<template>
  <CModal :show.sync="show" centered add-content-classes="fit-content-modal">
    <template #header>
      <div>
        <div class="d-flex">
          <CIcon name="cipRocket02" size="custom-size" width="36" height="36"
                 class="mr-3 icon-circle icon-circle--primary"/>
          <div class="align-self-center">
            <h5 class="modal-title" v-translate translate-context="event.modal.create_new_event.title">
              Activate event</h5>
            <div class="modal-subtitle" v-translate translate-context="event.modal.create_new_event.description">
              Review your settings and enable the event
            </div>
          </div>
        </div>
      </div>
      <CButtonClose @click="show = false"/>
    </template>
    <PComponentLoader :loading="loading">
      <template v-if="requireCertification">
        <div class="certification-disclaimer">
          <p class="mb-0" style="font-size: 14px; line-height: 1.5; color: #302F44;">
            <translate translate-context="event.modal.publish.certification">
              Creating an Event in Photo Buy mode is only allowed for VAT registered subjects (freelancers or legal entities) or otherwise subject to an equivalent tax regime under the applicable regulations. For further details, please refer to art. 5.3 of the
            </translate>
            <a :href="policyUrl.terms" target="_blank" class="certification-disclaimer__link ml-1">
              <translate translate-context="event.modal.publish.certification">T&amp;C</translate>
            </a>
          </p>
          <hr class="my-3" style="border-color: #CECCE8;" />
          <div class="d-flex align-items-center">
            <CInputCheckbox
              custom
              :checked.sync="certificationAccepted"
              id="certificationAccepted"
            />
            <label for="certificationAccepted" class="form-label mb-0" style="font-size: 14px; color: #302F44;">
              <translate translate-context="event.modal.publish.certification">I declare to be compliant</translate>
            </label>
          </div>
        </div>
        <div v-if="certificationAccepted" class="mt-3">
          <p class="mb-2" style="font-size: 13px; color: #6E6D7A;">
            <translate translate-context="event.modal.publish.certification">
              You can optionally provide your VAT data now. You can also do this later.
            </translate>
          </p>
          <VatDataForm
            ref="vatDataForm"
            v-model="vatForm"
            :contract-manager-id="contractManagerId"
            @validity-change="onVatDataFormValidity"
          />
        </div>
      </template>
      <template v-else-if="!allMandatoryIsPresent">
        <Suggestions :suggestions="suggestions"/>
      </template>
      <template v-else-if="configuration.is_in_payment">
        <div v-translate translate-context="event.modal.publish.info">
          Processing Payment, please wait...
        </div>
      </template>
      <template v-else-if="configuration.require_payment">
        <CategorySelector
            :categories="planOptions"
            :currency="paymentCurrency"
            :selected="planOptionId"
            @change="onSelectPlanOption"
        />
        <div style="padding: 12px 16px 0">
          <div class="font-weight-semi-bold mb-2">
            <translate translate-context="event.modal.create_new_event.plan_options_selector">
              Review your settings
            </translate>
          </div>
          <PAlert icon="cipInfo" color="info" class="mb-2">
            <template>
              <div v-translate translate-context="event.modal.publish.info">
                After activation you will no longer be able to change the date and time zone of the event
              </div>
            </template>
          </PAlert>
          <div class="event-recap mb-3">
          <div class="d-flex justify-content-between align-items-center">
            <div class="d-flex">
              <div class="label mr-2">
                <CIcon name="cipGlobe" size="custom-size" width="16" class="mr-1" style="vertical-align: sub"/>
                <translate translate-context="event.modal.publish.start_date">Date and time zone</translate>
              </div>
              <div class="value">{{ currentEventStartDate|formatDate('L') }} - {{ timeZone }}</div>
            </div>
          </div>
        </div>
        </div>
      </template>
      <template v-else>
        All ok, ready to publish!
        <Suggestions :suggestions="suggestions"/>
      </template>
    </PComponentLoader>
    <template #footer>
      <div class="d-flex justify-content-between w-100">
        <div class="mr-1">
          <CButton color="secondary" @click="show = false" v-translate>Cancel</CButton>
        </div>
        <div class="ml-1">
          <template v-if="requireCertification">
            <PButton
                color="primary"
                :disabled="!certificationAccepted"
                @click="onSubmitCertification">
              <translate translate-context="event.modal.publish.certification">Continue</translate>
            </PButton>
          </template>
          <template v-else-if="!allMandatoryIsPresent">
            <PButton
                v-if="hasPerm('advanced.force_publish')"
                color="danger" class="mr-3"
                @click="onPublish(true)">
              <translate>Force</translate>
            </PButton>
          </template>
          <template v-else-if="configuration.require_payment">
            <template v-if="isSubscription">
              <PButton
                  color="primary" class="ml-1"
                  :disabled="!planOptionId"
                  v-if="!configuration.is_in_payment"
                  @click="onPayment(false)">
                <translate translate-context="event.modal.publish.info">Subscribe monthly for</translate>
                {{ planPrice|currency(paymentCurrency) }}
              </PButton>
              <PButton
                  color="primary" class="ml-1"
                  :disabled="!planOptionId"
                  v-if="!configuration.is_in_payment"
                  @click="onPayment(true)">
                <translate translate-context="event.modal.publish.info">Subscribe yearly for</translate>
                {{ yearlyPrice|currency(paymentCurrency) }}
              </PButton>
            </template>
            <template v-else-if="planPrice > 0">
              <PButton
                  color="primary" class="ml-1"
                  :disabled="!planOptionId"
                  v-if="!configuration.is_in_payment"
                  @click="onPayment(false)">
                <translate translate-context="event.modal.publish.info">Activate for</translate>
                {{ planPrice|currency(paymentCurrency) }}
              </PButton>
            </template>
            <template v-else-if="planPrice <= 0">
              <PButton
                  color="primary" class="ml-1"
                  :disabled="!planOptionId"
                  @click="onPublish">
                <translate translate-context="event.modal.publish.info">Activate</translate>
              </PButton>
            </template>

          </template>
          <template v-else>
            <PButton
                v-if="allMandatoryIsPresent"
                color="primary" v-translate translate-context="event.modal.publish.actions" class="ml-1"
                @click="onPublish">
              Publish
            </PButton>
          </template>
        </div>
      </div>
    </template>
  </CModal>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import PButton from "@/components/PButton.vue";
import Suggestions from "@/domain/events/components/Suggestions.vue";
import PAlert from "@/components/PAlert.vue";
import PComponentLoader from "@/domain/core/components/PComponentLoader.vue";
import CategorySelector from "@/domain/events/components/CategorySelector.vue";
import VatDataForm from "@/components/VatDataForm.vue";

export default {
  name: "PublishEventModal",
  emits: ['published', 'edit', 'goToPayment'],
  components: {
    CategorySelector,
    PComponentLoader,
    PAlert,
    Suggestions,
    PButton,
    VatDataForm,
  },
  data() {
    return {
      show: false,
      loading: true,
      planOptionId: null,
      certificationAccepted: false,
      vatForm: {
        region: 'eu',
        vat_number: '',
        document: null
      },
      vatDataFormValid: false,
    }
  },
  computed: {
    ...mapGetters('eventWizard', [
      'allMandatoryIsPresent',
      'suggestions',
      'planOptions',
      'configuration',
      'paymentCurrency',
      'requireCertification',
    ]),
    ...mapGetters('event', [
      'currentEventStartDate',
      'timeZone',
      'contractManagerId',
    ]),
    policyUrl() {
      const lang = this.$language.current || 'en'
      return {
        terms: `https://policy.getpica.com/${lang}/termini-condizioni-vendita/`,
        privacy: `https://policy.getpica.com/${lang}/privacy-policy-pica/`,
      }
    },
    selectedOption() {
      if (!this.planOptionId) return null
      return this.planOptions
          .flatMap(category => category.base_plans)
          .find(plan => plan.id === this.planOptionId)
    },
    planPrice() {
      if (!this.selectedOption) return 0
      if (this.isSubscription) {
        return this.selectedOption.monthly_price || 0
      }
      return this.selectedOption.price
    },
    yearlyPrice() {
      if (!this.selectedOption) return 0
      if (this.isSubscription) {
        return this.selectedOption.yearly_price || 0
      }
      return 0
    },
    isSubscription() {
      if (!this.selectedOption) return false
      return this.selectedOption.is_subscription
    }
  },

  methods: {
    ...mapActions('eventWizard', [
      'publish',
      'fetchPlanOptions',
      'fetchPublishValidation',
      'fetchFormData',
      'fetchEventConfiguration',
      'fetchPaymentCurrency',
      'fetchRequireCertification',
      'acceptCertification',
    ]),
    onVatDataFormValidity(valid) {
      this.vatDataFormValid = valid
    },
    async open() {
      this.show = true
      this.loading = true
      this.certificationAccepted = false
      this.vatForm = { region: 'eu', vat_number: '', document: null }
      this.vatDataFormValid = false
      await this.fetchRequireCertification()
      if (!this.requireCertification) {
        await this.fetchPublishValidation()
        await this.fetchPaymentCurrency()
        if (this.allMandatoryIsPresent && this.configuration.require_payment)
          await this.fetchPlanOptions()
      }
      this.loading = false
    },
    async onSubmitCertification() {
      this.loading = true
      let certificationData = null
      if (this.vatDataFormValid && this.$refs.vatDataForm) {
        certificationData = this.$refs.vatDataForm.getFormData()
      }
      await this.acceptCertification(certificationData)
      await this.fetchPublishValidation()
      await this.fetchPaymentCurrency()
      if (this.allMandatoryIsPresent && this.configuration.require_payment)
        await this.fetchPlanOptions()
      this.loading = false
    },
    onSelectPlanOption(planId) {
      this.planOptionId = planId
    },
    async onPublish(force = false) {
      await this.publish({basePlanId: this.planOptionId, force: force})
      this.$emit('published')
      this.show = false
    },
    async onPayment(yearly = false) {
      this.$emit('goToPayment', {
        planId: this.planOptionId,
        isSubscription: this.isSubscription,
        yearly: yearly
      })
      this.show = false
    },
  }
}
</script>

<style scoped>
.certification-disclaimer {
  background-color: #F7FAFF;
  border: 1px solid #EDEBFF;
  border-radius: 12px;
  padding: 16px;
}

.certification-disclaimer__link {
  color: #1867FF;
  text-decoration: underline;
}
</style>
